import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import { fetchRddsRequest } from "../../../../redux/rdds/rddSlice";

import {
  fetchProjectDetailRequest,
  fetchProjectStakeholdersRequest,
  saveProjectStakeholdersRequest,
} from "../../../../redux/projects/projectsSlice";

import { fetchLocalTaskForceRolesRequest } from "../../../../redux/localTaskForceRoles/localTaskForceRoleSlice";
import { fetchDomainsRequest } from "../../../../redux/domains/domainSlice";

import StepLayout from "../../../../components/StepsLayout/StepsLayout";
// Custom Components
import PhaseArrow from "components/Common/PhaseArrow";
import FigmaBlueDivider from "components/StepComponents/FigmaBlueDivider";
import styles from "../StepOne/StepOnePage.module.css";
import DocumentItem from "components/Documents/StepZeroDocuments/stepZeroDocument";
import StakeholderEmailTemplate from "components/StepComponents/StakeholderEmailTemplate";
import LocalTaskForceSection from "components/StepOnePage/LocalTaskForceSection";
import StepOneDialog from "components/StepOnePage/StepOneDialog";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import step2TipsImage from "assets/images/RURACTIVEStep2Tips.png";
import phase32TipsImage from "assets/images/RURACTIVE_Phase3.2_Tips.png";

const documents = [
  {
    title: "RURACTIVE Communication Toolkit",
    file: "/documents/RURACTIVE_Communication_Toolkit.pdf",
    comingSoon: true,
  }
];

const StepTwoPage = () => {
  const { projectId } = useParams();
  const dispatch = useDispatch();

  const roles = useSelector(
    (state) => state.localTaskForceRoles?.items || []
  );

  const rdds = useSelector(
    (state) => state.rdds?.items || []
  );

  const domains = useSelector(
    (state) => state.domains?.items || []
  );

  const { currentProjectDetail } = useSelector(
    (state) => state.projects
  );

  const [stakeholders, setStakeholders] = useState([]);

  const [openModal, setOpenModal] = useState(false);
  const [openCommPopup, setOpenCommPopup] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(null);
  const [formData, setFormData] = useState(null);

  // =========================
  // ADD NEW LTF MEMBER
  // =========================
  const handleAddLocalTaskForceMember = () => {
    setFormData({
      id: null,
      first_name: "",
      last_name: "",
      is_local_task_force_member: true,
      local_task_force_role_id: null,
      contact_details: "",
      brief_description: "",
      rdd_id: null,
      domain_id: null,
      age: "",
      gender: "",
      grue: "",
      local_community_trainers: "",
      cross_cutting_priority: "",
      interest: 50,
      influence: 50,
    });

    setCurrentIndex(null);
    setOpenModal(true);
  };

  // =========================
  // SAVE MODAL
  // =========================
  const handleSaveModal = () => {
    let updated;

    if (currentIndex !== null) {
      updated = stakeholders.map((s, i) =>
        i === currentIndex ? formData : s
      );
    } else {
      updated = [...stakeholders, formData];
    }

    setStakeholders(updated);

    dispatch(
      saveProjectStakeholdersRequest({
        projectId,
        stakeholders: updated.map((s) => ({
          ...s,
          age: s.age === "" ? null : Number(s.age),
          gender: s.gender === "" ? null : s.gender,
          contact_details: s.contact_details || null,
          brief_description: s.brief_description || null,
          grue: s.grue || null,
          local_community_trainers:
            s.local_community_trainers || null,
          cross_cutting_priority:
            s.cross_cutting_priority || null,
          interest: s.interest ?? null,
          influence: s.influence ?? null,
        })),
      })
    );

    setOpenModal(false);
  };

  // =========================
  // FETCH DATA ON MOUNT
  // =========================
  useEffect(() => {
    dispatch(fetchLocalTaskForceRolesRequest());
    dispatch(fetchRddsRequest());
    dispatch(fetchDomainsRequest());

    if (projectId) {
      dispatch(fetchProjectDetailRequest({ projectId }));
      dispatch(fetchProjectStakeholdersRequest({ projectId }));
    }
  }, [dispatch, projectId]);

  // =========================
  // PREFILL STAKEHOLDERS
  // =========================
  useEffect(() => {
    if (currentProjectDetail?.stakeholders) {
      setStakeholders(currentProjectDetail.stakeholders);
    }
  }, [currentProjectDetail]);

  // =========================
  // DERIVED
  // =========================
  const localTaskForceMembers = stakeholders.filter(
    (s) => s.is_local_task_force_member
  );

  return (
    <StepLayout step={2}>
      <Box sx={{ textAlign: "center" }}>
        <Typography variant="h5" className={styles.projectTitle}>
          Step 2: Stakeholder Engagement
        </Typography>
      </Box>

      <Box sx={{ mt: 4, maxWidth: 900, mx: "auto" }}>

        {/* Introduction */}
        <Typography variant="h5" className={styles.introSubtitle}>
          Connect your RIE with the local community
        </Typography>

        <Typography variant="body1" className={styles.introText}>
          The stakeholders identified in Step 1 are now ready to be contacted and
          engaged.
        </Typography>

        <Typography variant="body1" className={styles.introText}>
          In this step, you will prepare your local communication approach,
          formally establish your Local Task Force, and reach out to stakeholders
          according to their level of involvement.
        </Typography>

        <Typography variant="body1" className={styles.introText}>
          The aim is to make the RIE known, build interest and participation, and
          begin activating the local community around your selected RDDs.
        </Typography>

        <Box className={styles.checklist}>
          <Typography variant="h6" className={styles.checklistTitle}>
            What you will complete
          </Typography>
          {[
            { text: "Prepare communication", desc: "Define the messages and channels you will use to reach your local community." },
            { text: "Enrol the Local Task Force", desc: "Contact your potential LTF members, confirm their participation and establish the working group." },
            { text: "Involve stakeholders", desc: "Contact your prioritised stakeholders and invite them to participate in the RIE and its activities." },
          ].map((item) => (
            <Box className={styles.checkItem} key={item.text}>
              <CheckCircleOutlineIcon className={styles.checkIcon} />
              <Box>
                <Typography className={styles.checkText}>{item.text}</Typography>
                <Typography className={styles.checkDesc}>{item.desc}</Typography>
              </Box>
            </Box>
          ))}
        </Box>

        <FigmaBlueDivider />

        {/* =========================
            PHASE 2.1 — COMMUNICATION
        ========================= */}
        <Typography
          variant="h6"
          sx={{ fontWeight: 600, mb: 1, mt: 4, color: "#23576B" }}
        >
          <PhaseArrow />
          Phase 2.1: Prepare communication
        </Typography>

        <Typography variant="h5" sx={{ fontWeight: 700, color: "#23576B", mb: 1 }}>
          Set up your local communication
        </Typography>

        <Typography variant="body1" className={styles.introText}>
          Effective engagement starts with communication that is clear, accessible
          and adapted to your local community.
        </Typography>

        <Typography variant="body1" className={styles.introText}>
          In this phase, the Local Communication Manager (LCM) coordinates the
          communication approach and prepares the Local Communication Plan (LCP).
        </Typography>

        <Typography variant="body1" className={styles.introText}>
          The LCP should explain the RIE, its goals and vision, and define the
          messages, materials and channels that will be used to invite people to
          participate and communicate future activities.
        </Typography>

        {/* ── INFO PANEL (learn) ── */}
        <Box className={styles.phaseInfoPanel}>
          <Box className={styles.phaseSectionLabel}>
            <Box className={styles.phaseSectionDot} />
            <Typography variant="h6" className={styles.phaseSectionTitle}>
              What should your communication achieve?
            </Typography>
          </Box>

          <Typography variant="body1" sx={{ color: "#7898A4", mb: 2 }}>
            Your communication should help people understand:
          </Typography>

          <Box className={styles.perspectiveGrid}>
            <Box className={styles.perspectiveCard}>
              <Typography variant="subtitle2" className={styles.perspectiveCardTitle}>
                What is the RIE?
              </Typography>
              <Typography variant="body2" className={styles.perspectiveCardDesc}>
                Explain the local RIE and the development priorities it addresses.
              </Typography>
            </Box>
            <Box className={styles.perspectiveCard}>
              <Typography variant="subtitle2" className={styles.perspectiveCardTitle}>
                Why does it matter?
              </Typography>
              <Typography variant="body2" className={styles.perspectiveCardDesc}>
                Communicate the local challenges, opportunities and expected benefits
                of the process.
              </Typography>
            </Box>
            <Box className={styles.perspectiveCard}>
              <Typography variant="subtitle2" className={styles.perspectiveCardTitle}>
                Why participate?
              </Typography>
              <Typography variant="body2" className={styles.perspectiveCardDesc}>
                Make clear how stakeholders and community members can contribute and
                what they can gain from participating.
              </Typography>
            </Box>
            <Box className={styles.perspectiveCard}>
              <Typography variant="subtitle2" className={styles.perspectiveCardTitle}>
                What happens next?
              </Typography>
              <Typography variant="body2" className={styles.perspectiveCardDesc}>
                Explain the upcoming activities, opportunities to participate and how
                people can stay informed.
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* ── COMMUNICATION CHANNELS ── */}
        <Box className={styles.phaseInfoPanel}>
          <Box className={styles.phaseSectionLabel}>
            <Box className={styles.phaseSectionDot} />
            <Typography variant="h6" className={styles.phaseSectionTitle}>
              Choose communication channels that people already use
            </Typography>
          </Box>

          <Typography variant="body1" sx={{ color: "#7898A4", mb: 2 }}>
            Use a combination of channels appropriate to your territory, such as:
          </Typography>

          <Box className={styles.perspectiveGrid}>
            {[
              "Organisation or project webpage",
              "Social media",
              "Leaflets, flyers, posters or roll-ups",
              "Local radio or television",
              "Local organisations and associations",
              "Local influencers",
              "Direct email, phone or messages",
              "Community meetings and local events",
            ].map((channel) => (
              <Box className={styles.perspectiveCard} key={channel}>
                <Typography variant="body2" className={styles.perspectiveCardDesc}>
                  {channel}
                </Typography>
              </Box>
            ))}
          </Box>

          <Box
            sx={{
              mt: 2,
              p: 2,
              borderRadius: "8px",
              backgroundColor: "#f5f7fb",
              borderLeft: "4px solid #35B3AB",
            }}
          >
            <Typography sx={{ fontSize: "0.85rem", fontStyle: "italic", color: "text.secondary", lineHeight: 1.6 }}>
              Use familiar local channels whenever possible. People are more likely
              to trust and engage with information communicated through channels
              they already know.
            </Typography>
          </Box>

          <Button
            variant="text"
            sx={{ mt: 2, color: "#35B3AB", textTransform: "none", fontWeight: 600 }}
            onClick={() => setOpenCommPopup(true)}
          >
            Learn more about communication channels →
          </Button>
        </Box>

        {/* ── INCLUSIVE COMMUNICATION ── */}
        <Box className={styles.phaseInfoPanel}>
          <Box className={styles.phaseSectionLabel}>
            <Box className={styles.phaseSectionDot} />
            <Typography variant="h6" className={styles.phaseSectionTitle}>
              Make communication inclusive
            </Typography>
          </Box>

          <Typography variant="body1" sx={{ color: "#7898A4", mb: 2 }}>
            Your communication should make it clear that participation is open to
            the whole community. Pay particular attention to groups that may have
            been historically excluded or underrepresented.
          </Typography>

          <Typography variant="body1" sx={{ color: "#7898A4", mb: 1 }}>
            Consider:
          </Typography>

          {[
            "Using accessible language",
            "Choosing channels that reach different groups",
            "Working with intermediary organisations",
            "Engaging local influencers",
            "Organising targeted information activities",
            "Clearly communicating your commitment to gender balance and inclusion",
          ].map((tip) => (
            <Box className={styles.checkItem} key={tip}>
              <CheckCircleOutlineIcon className={styles.checkIcon} />
              <Typography className={styles.checkText}>{tip}</Typography>
            </Box>
          ))}

          <Typography variant="body1" sx={{ color: "#7898A4", mt: 2 }}>
            Different groups may need different communication approaches. Adapt your
            message and channel to the people you want to reach.
          </Typography>
        </Box>

        {/* ── SUPPORT MATERIAL ── */}
        
        <Typography variant="h6" sx={{ fontWeight: 600, color: "#7898A4", mb: 2, mt: 4 }}>
          Support Material:
        </Typography>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {documents.map((doc, index) => (
            <DocumentItem key={index} title={doc.title} file={doc.file} comingSoon={doc.comingSoon} />
          ))}
        </Box>
        

        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 1, mt: 4, mb: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: "#23576B" }}>
            TIPS
          </Typography>
        </Box>

        <Box sx={{ display: "flex", justifyContent: "center" }}>
          <Box
            component="img"
            src={step2TipsImage}
            alt="Step 2 tips"
            sx={{
              width: "100%",
              maxWidth: 880,
              borderRadius: "20px",
              boxShadow: "0 8px 24px rgba(19, 168, 158, 0.1), 0 4px 12px rgba(0, 0, 0, 0.04)",
            }}
          />
        </Box>
        
        {/* ── COMMUNICATION POPUP ── */}
        <Dialog
          open={openCommPopup}
          onClose={() => setOpenCommPopup(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ fontWeight: 700, color: "#23576B" }}>
            Communication for different stakeholders
          </DialogTitle>
          <DialogContent>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#23576B", mb: 1, mt: 1 }}>
              Match the channel to the level of involvement
            </Typography>
            <Typography variant="body2" sx={{ color: "#7898A4", mb: 2 }}>
              Your stakeholder prioritisation can guide how you communicate.
            </Typography>

            {[
              {
                level: "INFORM",
                desc: "Use broad channels such as social media, webpages, radio/TV, public calls and local organisations.",
              },
              {
                level: "CONSULT / INVOLVE",
                desc: "Use more direct communication such as email, phone calls or messages.",
              },
              {
                level: "COLLABORATE",
                desc: "Use direct contact and consider personal or informal meetings.",
              },
            ].map((item) => (
              <Box className={styles.popupLevelCard} key={item.level} sx={{ mb: 2 }}>
                <Typography variant="subtitle2" className={styles.popupLevelLabel}>
                  {item.level}
                </Typography>
                <Typography variant="body2" className={styles.popupLevelDesc}>
                  {item.desc}
                </Typography>
              </Box>
            ))}

            <Typography variant="body2" sx={{ color: "#7898A4", mt: 1 }}>
              You don't need to use the same communication approach for everyone.
              The more actively you want someone to participate, the more personal
              and direct the communication can become.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setOpenCommPopup(false)}
              sx={{ color: "#35B3AB", textTransform: "none", fontWeight: 600 }}
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>

        <FigmaBlueDivider />

        {/* =========================
            PHASE 2.2 — ENROL LOCAL TASK FORCE
        ========================= */}
        <Typography
          variant="h6"
          sx={{ fontWeight: 600, mb: 1, mt: 4, color: "#23576B" }}
        >
          <PhaseArrow />
          Phase 2.2: Enrol the Local Task Force
        </Typography>

        <Typography variant="h5" sx={{ fontWeight: 700, color: "#23576B", mb: 1 }}>
          Build your core working group
        </Typography>

        <Typography variant="body1" className={styles.introText}>
          Your potential Local Task Force members were identified in Step 1. Now it
          is time to contact them, introduce the RIE and confirm their willingness
          to participate.
        </Typography>

        <Typography variant="body1" className={styles.introText}>
          The enrolment process should give potential members a clear understanding
          of the project, their role, expected activities, timeline and outputs
          before they commit.
        </Typography>

        {/* ── INFO PANEL (learn) ── */}
        <Box className={styles.phaseInfoPanel}>
          <Box className={styles.phaseSectionLabel}>
            <Box className={styles.phaseSectionDot} />
            <Typography variant="h6" className={styles.phaseSectionTitle}>
              How to enrol the Local Task Force
            </Typography>
          </Box>

          <Typography variant="body1" sx={{ color: "#7898A4", mb: 3 }}>
            The enrolment process follows three simple stages.
          </Typography>

          {[
            {
              num: "01",
              title: "Initial contact",
              points: [
                "The RIE and its objectives",
                "Why you are contacting them",
                "Why their contribution is relevant",
                "What participation could involve",
                "The benefits of joining the LTF",
              ],
              note: "Contact each potential LTF member by email, phone or message. Briefly explain:",
            },
            {
              num: "02",
              title: "Introductory meeting",
              points: [
                "Your organisation",
                "The RIE and its objectives",
                "The Local Task Force",
                "Expected activities",
                "The expected effort and timeline",
                "Expected outputs",
                "Benefits of participating",
              ],
              note: "Invite interested stakeholders to an informal introductory meeting. Present:",
            },
            {
              num: "03",
              title: "Formal commitment",
              points: [],
              note: "Stakeholders who agree to participate should confirm their willingness by signing a Letter of Intent / commitment of intent. This confirms their intention to actively participate as members of the Local Task Force.",
            },
          ].map((stage, index, arr) => (
            <Box key={stage.num} sx={{ display: "flex", gap: 2, mb: index < arr.length - 1 ? 0 : 0 }}>
              {/* Timeline indicator */}
              <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", minWidth: 48 }}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: "50%",
                    background: "linear-gradient(135deg, #35B3AB, #23576B)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#fff",
                    fontWeight: 700,
                    fontSize: "0.9rem",
                    flexShrink: 0,
                    boxShadow: "0 2px 8px rgba(53, 179, 171, 0.3)",
                  }}
                >
                  {stage.num}
                </Box>
                {index < arr.length - 1 && (
                  <Box
                    sx={{
                      width: 2,
                      flex: 1,
                      minHeight: 40,
                      background: "linear-gradient(to bottom, #35B3AB, rgba(53, 179, 171, 0.2))",
                    }}
                  />
                )}
              </Box>

              {/* Step content */}
              <Box
                sx={{
                  flex: 1,
                  p: 2.5,
                  mb: index < arr.length - 1 ? 3 : 0,
                  border: "1px solid rgba(35, 87, 107, 0.1)",
                  borderRadius: "16px",
                  background: "linear-gradient(135deg, rgba(35, 87, 107, 0.03), rgba(53, 179, 171, 0.02))",
                }}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#23576B", mb: 1 }}>
                  {stage.title}
                </Typography>
                <Typography variant="body2" sx={{ color: "#7898A4", mb: stage.points.length > 0 ? 1.5 : 0, lineHeight: 1.7 }}>
                  {stage.note}
                </Typography>
                {stage.points.length > 0 && (
                  <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                    {stage.points.map((p) => (
                      <Box component="li" key={p} sx={{ mb: 0.25 }}>
                        <Typography variant="body2" sx={{ color: "#7898A4" }}>
                          {p}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            </Box>
          ))}

          <Button
            variant="text"
            sx={{ mt: 2, color: "#35B3AB", textTransform: "none", fontWeight: 600 }}
            href="/documents/Letter_of_Intent_template.pdf"
            target="_blank"
            rel="noopener noreferrer"
          >
            Download Letter of Intent template →
          </Button>
        </Box>

        {/* ── DATA ENTRY (established members) ── */}
        <Box className={styles.phaseDataPanel}>
          <Box className={styles.phaseSectionLabel}>
            <Box className={styles.phaseSectionDot} />
            <Typography variant="h6" className={styles.phaseSectionTitle}>
              Enrol
            </Typography>
          </Box>

          <Typography variant="h5" sx={{ fontWeight: 700, color: "#23576B", mb: 1 }}>
            Enrol your Local Task Force
          </Typography>

          <Typography variant="body1" className={styles.introText} sx={{ mb: 3 }}>
            Your potential LTF members from Step 1 are listed below.
          </Typography>

          <LocalTaskForceSection
            stakeholders={stakeholders}
            setStakeholders={setStakeholders}
            localTaskForceMembers={localTaskForceMembers}
            rdds={rdds}
            roles={roles}
            projectId={projectId}
            dispatch={dispatch}
            onAddNewMember={handleAddLocalTaskForceMember}
            setFormData={setFormData}
            setCurrentIndex={setCurrentIndex}
            setOpenModal={setOpenModal}
          />
        </Box>

        {/* =========================
            STAKEHOLDER MODAL
        ========================= */}
        <StepOneDialog
          open={openModal}
          onClose={() => setOpenModal(false)}
          onSave={handleSaveModal}
          formData={formData}
          setFormData={setFormData}
          currentIndex={currentIndex}
          rdds={rdds}
          domains={domains}
        />

        <FigmaBlueDivider sx={{ mt: 6 }} />

        {/* =========================
            PHASE 2.3 — INVOLVE STAKEHOLDERS
        ========================= */}
        <Typography
          variant="h6"
          sx={{ fontWeight: 600, mb: 1, mt: 4, color: "#23576B" }}
        >
          <PhaseArrow />
          Phase 2.3: Involve stakeholders
        </Typography>

        <Typography variant="h5" sx={{ fontWeight: 700, color: "#23576B", mb: 1 }}>
          Activate your wider stakeholder community
        </Typography>

        <Typography variant="body1" className={styles.introText}>
          The Local Task Force is now being established. The next step is to reach
          the wider stakeholder community and invite people and organisations to
          participate in the RIE.
        </Typography>

        <Typography variant="body1" className={styles.introText}>
          Use the priorities established in Step 1 to decide who to contact, how
          to approach them and how actively to involve them.
        </Typography>

        <Typography variant="body1" className={styles.introText}>
          This is not a one-time activity. The stakeholder database can be updated
          as new people become interested or existing stakeholder information
          changes.
        </Typography>

        {/* ── INFO PANEL: Who should you involve? ── */}
        <Box className={styles.phaseInfoPanel}>
          <Box className={styles.phaseSectionLabel}>
            <Box className={styles.phaseSectionDot} />
            <Typography variant="h6" className={styles.phaseSectionTitle}>
              Who should you involve?
            </Typography>
          </Box>

          <Typography variant="body1" sx={{ color: "#7898A4", mb: 2 }}>
            Start with the stakeholder list you prioritised in Step 1.
          </Typography>

          <Box className={styles.involvementGrid}>
            {[
              { level: "Collaborate", desc: "Contact directly and consider personal or informal meetings." },
              { level: "Involve", desc: "Use direct communication such as email, phone or messages and invite active participation." },
              { level: "Consult", desc: "Contact directly to seek their knowledge, views or feedback." },
              { level: "Inform", desc: "Use broader communication channels to keep them aware of the RIE and its activities." },
            ].map((item) => (
              <Box className={styles.involvementCard} key={item.level}>
                <Typography variant="subtitle2" className={styles.involvementLabel}>
                  {item.level}
                </Typography>
                <Typography variant="body2" className={styles.involvementDesc}>
                  {item.desc}
                </Typography>
              </Box>
            ))}
          </Box>

          <Typography variant="body1" sx={{ color: "#7898A4", mb: 2 }}>
            The aim is not simply to contact everyone in the same way. Match your
            engagement approach to each stakeholder's expected level of
            involvement.
          </Typography>

        </Box>

        {/* ── INFO PANEL: Widen the RIE community ── */}
        <Box className={styles.phaseInfoPanel}>
          <Box className={styles.phaseSectionLabel}>
            <Box className={styles.phaseSectionDot} />
            <Typography variant="h6" className={styles.phaseSectionTitle}>
              Widen the RIE community
            </Typography>
          </Box>

          <Typography variant="body1" sx={{ color: "#7898A4", mb: 2 }}>
            Your stakeholder database is a starting point, not a closed list. Ask
            the LTF and local community who else should be involved.
          </Typography>

          <Typography variant="body1" sx={{ color: "#7898A4", mb: 1 }}>
            You can also reach new stakeholders through:
          </Typography>

          {[
            "Local associations and intermediary organisations",
            "Local influencers",
            "Community locations",
            "Direct visits",
            "Small local information meetings",
            "Existing community networks",
          ].map((item) => (
            <Box className={styles.checkItem} key={item}>
              <CheckCircleOutlineIcon className={styles.checkIcon} />
              <Typography className={styles.checkText}>{item}</Typography>
            </Box>
          ))}

          <Typography variant="body1" sx={{ color: "#7898A4", mt: 2 }}>
            If someone new shows interest, add them to your stakeholder database
            and assess their relevance to the RIE.
          </Typography>
        </Box>

        {/* ── INFO PANEL: The Open Day ── */}
        <Box className={styles.phaseInfoPanel}>
          <Box className={styles.phaseSectionLabel}>
            <Box className={styles.phaseSectionDot} />
            <Typography variant="h6" className={styles.phaseSectionTitle}>
              The Open Day
            </Typography>
          </Box>

          <Typography variant="body1" sx={{ color: "#7898A4", mb: 2 }}>
            The engagement phase culminates in the Open Day, the local launch event
            of the RIE. The event introduces the RIE and its activities to the
            wider community.
          </Typography>

          <Typography variant="body1" sx={{ color: "#7898A4", mb: 1 }}>
            During the Open Day:
          </Typography>

          {[
            "Present the RIE and its objectives",
            "Introduce the Local Task Force",
            "Explain upcoming activities",
            "Explain stakeholder roles and expectations",
            "Share the expected timeline and outputs",
            "Give participants an opportunity to ask questions",
            "Invite interested participants to confirm their willingness to participate",
          ].map((item) => (
            <Box className={styles.checkItem} key={item}>
              <CheckCircleOutlineIcon className={styles.checkIcon} />
              <Typography className={styles.checkText}>{item}</Typography>
            </Box>
          ))}

          <Typography variant="body1" sx={{ color: "#7898A4", mt: 2 }}>
            The LTF can also remain flexible: if new stakeholders express interest
            in joining it, consider whether they should be included.
          </Typography>

        </Box>

        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 1, mt: 4, mb: 1 }}>
          <Box sx={{ width: 8, height: 8, borderRadius: "50%", background: "#35B3AB" }} />
          <Typography variant="h6" sx={{ fontWeight: 700, color: "#23576B" }}>
            TIPS
          </Typography>
        </Box>

        <Box sx={{ display: "flex", justifyContent: "center" }}>
          <Box
            component="img"
            src={phase32TipsImage}
            alt="Phase 2.3 tips"
            sx={{
              width: "60%",
              maxWidth: 880,
              borderRadius: "20px",
              boxShadow: "0 8px 24px rgba(19, 168, 158, 0.1), 0 4px 12px rgba(0, 0, 0, 0.04)",
            }}
          />
        </Box>

        {/* ── DATA ENTRY ── */}
        {/*
        <StakeholderEmailTemplate />
        */}
      </Box>
    </StepLayout>
  );
};

export default StepTwoPage;